import type {UseScrollTriggerOptions} from "@mui/material/useScrollTrigger/useScrollTrigger";
import {useContext, useEffect, useRef, useState} from "react";

import ScrollContext from "./ScrollContext";

// Based on https://github.com/mui/material-ui/blob/v6.x/packages/mui-material/src/useScrollTrigger/useScrollTrigger.js

function defaultTrigger(
	store: {current: number},
	target: Window,
	threshold: number,
	disableHysteresis?: boolean
) {
	const previous = store.current;

	if (target) {
		store.current = target.scrollY;
	}

	if (!disableHysteresis) {
		if (store.current < previous) {
			return false;
		}
	}

	return store.current > threshold;
}

function useScrollTrigger(
	options: Omit<UseScrollTriggerOptions, "target"> = {}
) {
	const {disableHysteresis, threshold = 100} = options;

	const target = window;

	const store = useRef(0);

	const [trigger, setTrigger] = useState(() =>
		defaultTrigger(store, target, threshold, disableHysteresis)
	);

	const {forced} = useContext(ScrollContext);

	useEffect(() => {
		const handleScroll = () => {
			if (!forced()) {
				setTrigger(defaultTrigger(store, target, threshold, disableHysteresis));
			}
		};

		handleScroll();
		target.addEventListener("scroll", handleScroll, {passive: true});

		return () => {
			target.removeEventListener("scroll", handleScroll);
		};
	}, [disableHysteresis, forced, target, threshold]);

	return trigger;
}

export default useScrollTrigger;
